import React, { useState, useRef, useEffect } from "react";
import { Flex, Divider, Text, Accordion, Pill, Alert, Button } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { IsAppendTableContext } from "@coreapp/utils/IsAppendTableContext";
import { useRecordEditorController } from "./useRecordEditorController";
import { ProcessAdvancedField, GetSuperIDName } from "./RecordEditor.utils";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import {
  kPositionID,
  kParentPositionID,
  kPositionLocation,
  kPositionStartDate,
  kPositionEndDate,
  kPositionTitleID,
  kPositionDivisionID,
  kDesiredHolders,
  kPositionCostBandMinimum,
  kPositionCostBandMaximum,
  kPositionBudgetedSalary,
  enumObjClassNames,
  enumOverrideType,
  enumObjectTypes,
  kEmployeeID,
  BOX_ACTIONS,
  kBlockedFieldTag
} from "@coreapp/constants";
import {
  GetPositionFieldsConfig,
  BuildPositionFieldMap,
  BuildPositionRangeFieldsMap,
  doesErrorApplyToField,
  isUserMappedField,
  isCostBandMapped,
  calculateTotalPositionBudget
} from "./PositionEditorUtils";

import BasicInfoSection from "./Components/BasicInfoSection";
import AssignmentSection from "./Components/AssignmentSection";
import BudgetDetailsDrawer from "./Components/BudgetDetailsDrawer";
import { PositionEditorLeftSidebar } from "@/appComponents/Blocks/Modals/RecordEditorModal/Components/PositionEditorLeftSidebar";
import BaseRecordEditorModal from "./BaseRecordEditorModal";
import { DoValidationAlert } from "./RecordEditor.utils";
import cx from "classnames";
import { useTopic } from "@/hooks/useTopic";
import { DefaultCardDimensions } from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";
import { useEmployeeAssignment } from "@/hooks/useEmployeeAssignment";
import { ConflictAssignmentModal } from "@/appComponents/Navigation/RightSidebar/Planning/EmployeeList/ConflictAssignmentModal";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { usePositionRecall } from "@/hooks/usePositionRecall";
import { isInPlanning } from "@coreapp/utils/isInPlanning";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { calculateModalSize } from "./ModalSizing.utils";
import AdditionalInfoSection from "./Components/AdditionalInfoSection";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";

const enumPositionFieldIDs = {
  positionID: kPositionID,
  parentPositionID: kParentPositionID,
  title: kPositionTitleID,
  division: kPositionDivisionID,
  location: kPositionLocation,
  desiredHolders: kDesiredHolders,
  costBandMin: kPositionCostBandMinimum,
  costBandMax: kPositionCostBandMaximum,
  startDate: kPositionStartDate,
  endDate: kPositionEndDate
};

const applyRevertButtonPlacement = (field, { isEditable, isDateField, revertButton }) => {
  if (!isEditable) {
    return {
      ...field,
      revertButton: null,
      textEditOptions: {
        ...field.textEditOptions,
        inputProps: { ...field.textEditOptions?.inputProps, postfixNode: null }
      }
    };
  }
  // Date inputs already use the postfix slot for the calendar picker, so keep
  // the revert button as a sibling instead of colliding with it. SupervisorFieldInput
  // builds its own inputProps and never forwards postfixNode, so it needs the sibling
  // slot too — routing it through postfix dropped the button entirely.
  if (isDateField || field.textEditOptions?.isSupervisorField) {
    return { ...field, revertButton };
  }
  return {
    ...field,
    revertButton: null,
    textEditOptions: {
      ...field.textEditOptions,
      inputProps: { ...field.textEditOptions?.inputProps, postfixNode: revertButton }
    }
  };
};

const PositionEditorModal = ({
  theData,
  showPositionEditor,
  onSaveRecordEdit: onSaveRecordEditCtx,
  onClosePositionEditor,
  manager,
  queryChartId = null
}) => {
  const positionData = theData[enumObjectTypes.position];
  const action = positionData?.action;

  const dbObj = positionData?.dataObj;
  const overrideObj = positionData?.override;
  const type = positionData?.type;
  const initChanges = positionData?.initChanges || {};
  const isMultiEdit = positionData?.isMultiEdit || false;
  const multiDataObjs = positionData?.multiDataObjs || null;
  const wsMgr = GetWSMgr();

  const { initialWidth, initialHeight } = calculateModalSize();

  const [errors, setErrors] = useState([]);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [originalAssignedEmployees, setOriginalAssignedEmployees] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [sidebarMode, setSidebarMode] = useState("budget");
  const [isInitialized, setIsInitialized] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const isPlanningContext = isInPlanning();
  const renderSidebar = !isMultiEdit && isPlanningContext;
  const overrideHasConflicts = manager?.OverrideHasConflicts(overrideObj);

  const { conflictContextValue } = usePlanManagerContext();
  const { setShowConflictModal, setConflictOverride } = conflictContextValue;

  const {
    record,
    changeResults,
    baseRecord,
    recordSupervisor,
    baseRecordSupervisor,
    override,
    existingSupervisorID,
    changeType,
    madeFieldReverts,
    workingOverride,
    IsOnlyCommentChange,
    comment,
    UpdateComment,
    CommentOnlyChange,
    existingComment,
    UpdateFieldValue,
    CheckCoreValueSet,
    updateRecordSupervisorAsDict,
    GetCurrentFieldValue,
    GetActiveChangeValueByField,
    SaveRecordChanges,
    LoadSupervisorDetails,
    InitializeTarget,
    UnsavedChanges,
    changeTarget,
    DoRevertAll,
    updateRecordEditorState,
    GetExistingFieldChanges,
    unsavedFieldChange,
    processAssignmentChanges,
    GetHolder,
    setMadeFieldReverts,
    GetBaseFieldValue,
    GetOverrideStopIndex
  } = useRecordEditorController({
    dbObj,
    overrideObj,
    type,
    manager,
    initChanges,
    isPlanEdit: true,
    isMultiEdit,
    multiDataObjs
  });

  const { handleAddEmployee, handleRemoveEmployee, forceAssignEmployees } = useEmployeeAssignment({
    assignedEmployees,
    setAssignedEmployees,
    currentPosition: dbObj,
    overrides: [],
    GetHolder,
    onConflict: conflictEmployees => {
      setConflicts(conflictEmployees);
    },
    immediateConflictDetection: true
  });

  const { GetRevertButton, IsDateField } = usePositionRecall({
    dbObj,
    manager,
    baseRecordSupervisor,
    workingOverride,
    isMultiEdit,
    IsOnlyCommentChange,
    GetExistingFieldChanges,
    updateRecordEditorState,
    DoRevertAll,
    handleClose: () => {
      setSidebarExpanded(false);
      setIsInitialized(false);
      onClosePositionEditor();
    },
    wsMgr,
    setMadeFieldReverts,
    GetBaseFieldValue,
    GetOverrideStopIndex,
    LoadSupervisorDetails,
    updateRecordSupervisorAsDict,
    GetActiveChangeValueByField,
    changeResults
  });

  const handleClose = React.useCallback(() => {
    setSidebarExpanded(false);
    setIsInitialized(false);
    onClosePositionEditor();
  }, [setSidebarExpanded, setIsInitialized, onClosePositionEditor]);

  // Prepare controller data to pass to BaseRecordEditorModal
  const controllerData = {
    record,
    baseRecord,
    recordSupervisor: baseRecordSupervisor,
    override,
    existingSupervisorID,
    changeResults,
    changeType,
    madeFieldReverts,
    workingOverride,
    IsOnlyCommentChange,
    CommentOnlyChange,
    comment,
    UpdateComment,
    existingComment
  };

  const onSaveRecordEdit = async data => {
    await onSaveRecordEditCtx(data, action);
  };

  // Controller functions to pass to BaseRecordEditorModal
  const handleUnsavedChanges = () => UnsavedChanges();

  const handleRevertOverride = () => DoRevertAll();

  const handleInitializeTarget = (newOverrideObj, newDbObj, loadRecord) => {
    const result = changeTarget(() => {}, newOverrideObj, newDbObj, loadRecord);
    return result;
  };

  const overrideObjRef = useRef(null);
  overrideObjRef.current = overrideObj;

  const recordRef = useRef(null);
  recordRef.current = record;

  const originalAssignedEmployeesRef = useRef([]);
  originalAssignedEmployeesRef.current = originalAssignedEmployees;

  const assignedEmployeesRef = useRef([]);
  assignedEmployeesRef.current = assignedEmployees;

  const dbObjRef = useRef(null);
  dbObjRef.current = dbObj;

  const hydrateAssignee = empData => {
    const doc = GetDoc();
    const boxClass = doc?.GetBoxClass();
    const empObj = new QObjDBObject();
    empObj.InitObject(boxClass);
    boxClass?.ProcessObject(empObj, empData);
    return empObj;
  };

  const refreshEmployeeData = (empId, mgr) => {
    const doc = GetDoc();
    const boxClass = doc?.GetBoxClass();
    const recordIdFieldDef = boxClass?.GetRecordIDFieldDef();
    const updatedEmp = boxClass?.FindObject(recordIdFieldDef, empId, false);

    if (updatedEmp) {
      const clonedEmp = updatedEmp.Clone(true);

      const override = mgr?.fOverrideClass?.GetExistingBoxOverride(clonedEmp);
      if (override) {
        override.ApplyChangesReadOnly(clonedEmp);
      }

      setAssignedEmployees(prevState => {
        const newState = prevState.map(prevEmp => {
          const prevEmpId = prevEmp.SmartGetFieldValue(kEmployeeID);
          return prevEmpId === empId ? clonedEmp : prevEmp;
        });
        return [...newState];
      });
    } else {
      mgr?.LoadRecords(
        { recordIDs: [empId] },
        response => {
          const responseData = typeof response === "string" ? JSON.parse(response) : response;
          if (responseData?.data && responseData.data[0]) {
            const loadedEmp = hydrateAssignee(responseData.data[0]);

            const override = mgr?.fOverrideClass?.GetExistingBoxOverride(loadedEmp);
            if (override) {
              override.ApplyChangesReadOnly(loadedEmp);
            }

            setAssignedEmployees(prevState => {
              const newState = prevState.map(prevEmp => {
                const prevEmpId = prevEmp.SmartGetFieldValue(kEmployeeID);
                return prevEmpId === empId ? loadedEmp : prevEmp;
              });
              return [...newState];
            });
          }
        },
        false
      );
    }
  };

  useTopic("loadedOverrides", payload => {
    const { planMgr } = payload;
    if (!planMgr) return;

    const currentEmployees = assignedEmployeesRef.current;
    const doc = GetDoc();
    const boxClass = doc?.GetBoxClass();
    const recordIdFieldDef = boxClass?.GetRecordIDFieldDef();
    const employeesNeedingRFC = [];

    const updatedEmployees = currentEmployees.map(emp => {
      const empId = emp.SmartGetFieldValue(kEmployeeID);
      const updatedEmp = boxClass?.FindObject(recordIdFieldDef, empId, false);

      if (updatedEmp) {
        const clonedEmp = updatedEmp.Clone(true);

        const override = planMgr?.fOverrideClass?.GetExistingBoxOverride(clonedEmp);
        if (override) {
          override.ApplyChangesReadOnly(clonedEmp);
        }

        return clonedEmp;
      } else {
        employeesNeedingRFC.push(empId);
        return emp;
      }
    });

    setAssignedEmployees(() => updatedEmployees);

    employeesNeedingRFC.forEach(empId => {
      refreshEmployeeData(empId, planMgr);
    });
  });

  const handleLoadRecordFallback = (recordID, isPositionObj) => {
    const setRecordFallbackCallback = r => {
      let resObject = typeof r === "string" ? JSON.parse(r) : r;
      if (!resObject.positions || !resObject.positions[0]) return;
      updateRecordEditorState({ fetchedRecordFallback: resObject.positions[0] ?? {} });
    };

    // Include holders when loading positions for fallback
    manager?.LoadRecords?.(
      { positionIDs: [recordID], includeHolders: true },
      r => setRecordFallbackCallback(r),
      isPositionObj
    );
  };

  const canManageFields = isInPlanning()
    ? getPlanPermissions({ useCurrentPlan: true }).canManageFields
    : false;

  // For position plans, specifically get additional fields for positions
  const additionalFields = manager?.fPlanInfo?.fields?.position ?? [];

  const handleAssignEmployee = async employeeData => {
    return await handleAddEmployee(employeeData);
  };

  const handleRemoveEmployeeWrapper = employee => {
    handleRemoveEmployee(employee);
  };

  useEffect(() => {
    if (dbObj && overrideObj && manager && showPositionEditor && !isInitialized) {
      // First initialize the target
      InitializeTarget(overrideObj, dbObj, true);
      // Then explicitly load supervisor details
      LoadSupervisorDetails(overrideObj);

      // Initialize assigned employees from the position's holder list
      const holders = dbObj.GetHolderList() || [];
      setAssignedEmployees(holders);
      setOriginalAssignedEmployees(holders);
      setIsInitialized(true);
    }
  }, [
    dbObj,
    overrideObj,
    manager,
    showPositionEditor,
    isInitialized,
    InitializeTarget,
    LoadSupervisorDetails
  ]);

  useTopic("positionLoadedWithHolders", ({ positionId, holders }) => {
    const currentDbObj = dbObjRef.current;
    if (!currentDbObj) {
      return;
    }

    const currentPositionId = currentDbObj?.SmartGetFieldValue(kPositionID);
    if (currentPositionId !== positionId) {
      return;
    }

    if (holders && holders.length > 0) {
      const currentAssignedEmployees = assignedEmployeesRef.current;
      if (
        currentAssignedEmployees.length !== holders.length ||
        currentAssignedEmployees.some((emp, idx) => emp !== holders[idx])
      ) {
        setAssignedEmployees(holders);
        setOriginalAssignedEmployees(holders);
      }
    }
  });

  if (!manager || !dbObj || !overrideObj || !workingOverride || !showPositionEditor) {
    return null;
  }

  const validateFields = () => {
    let failureArr = [];

    // Validate required fields
    const positionTitle = CheckCoreValueSet(enumPositionFieldIDs.title);
    if (!positionTitle || !positionTitle.trim()) {
      failureArr.push(appstr("Position Title is required"));
    }

    const parentPositionID = CheckCoreValueSet(enumPositionFieldIDs.parentPositionID);
    if (!parentPositionID) {
      failureArr.push(appstr("Reports To is required"));
    }

    // Validate Staffing Target
    const staffingTarget = CheckCoreValueSet(enumPositionFieldIDs.desiredHolders);
    if (staffingTarget) {
      const staffingTargetVal = parseInt(staffingTarget) ?? 0;
      if (staffingTargetVal <= 0) {
        failureArr.push(appstr("staffingtargeterror"));
      }
    }

    // Validate Start and End Dates
    const startDate = CheckCoreValueSet(enumPositionFieldIDs.startDate);
    const endDate = CheckCoreValueSet(enumPositionFieldIDs.endDate);
    if (startDate && endDate) {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      if (startDateObj > endDateObj) {
        failureArr.push(appstr("startenddateerror"));
      }
    }

    // Validate Cost Band
    const costBandMin = CheckCoreValueSet(enumPositionFieldIDs.costBandMin);
    const costBandMax = CheckCoreValueSet(enumPositionFieldIDs.costBandMax);
    if (costBandMin && costBandMax) {
      if (parseFloat(costBandMin) > parseFloat(costBandMax)) {
        failureArr.push(appstr("costbanderror"));
      }
    }

    return failureArr;
  };

  // Custom save handler that uses the centralized save routine
  const handleSave = async (validate = true) => {
    // First check field validation
    if (validate) {
      const validationErrors = validateFields();
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        DoValidationAlert(validationErrors);
        return { success: false };
      }
    }

    // Process assignment changes separately
    const assignmentChanges = processAssignmentChanges(
      assignedEmployees,
      originalAssignedEmployees
    );

    // Include additional fields in the changes to be saved
    const additionalData = {};
    const additionalChanges = {};

    // Check if Parent PositionID field changed
    const parentPositionFieldName = GetSuperIDName(dbObj);

    // If parent position changed, we need to change the override type to "move"
    if (changeResults[parentPositionFieldName] != null) {
      additionalData.changeType = enumOverrideType.move;
    }

    // If we have additional fields, make sure they're included in the save
    if (additionalFields && additionalFields.length > 0) {
      // We don't need to explicitly add them here since they're already part of the
      // working override through the UpdateFieldValue calls
      // This ensures all fields (basic and additional) are saved together
    }

    // Use the centralized save routine
    const result = await SaveRecordChanges(
      onSaveRecordEdit,
      { assignmentChanges, ...additionalData }, // Pass the assignment changes information
      validate,
      false, // Don't skip validation
      additionalChanges
    );

    if (result && !result.success && result.errors) {
      setErrors(result.errors);
      DoValidationAlert(result.errors);
      return { success: false };
    }

    // If save was successful, update the original assigned employees
    if (result && result.success) {
      setOriginalAssignedEmployees(assignedEmployees);
    }

    setIsInitialized(false); // Reset initialization state

    return result;
  };

  const searchClickHandler = target => {
    updateRecordSupervisorAsDict(target.result);
    UpdateFieldValue({ field: GetSuperIDName(dbObj), value: target.value });
  };

  // Handler for removing parent position (clear button functionality)
  const handleRemoveParentPosition = () => {
    const parentPositionFieldName = GetSuperIDName(dbObj);
    UpdateFieldValue({ field: parentPositionFieldName, value: "" });
    updateRecordSupervisorAsDict(null);
  };

  const handleFieldValueUpdate = ({ field, value }) => {
    if (!isInitialized) {
      return;
    }

    // Check if this is actually a change from the current field value
    const currentValue = GetCurrentFieldValue(field);
    if (currentValue == value) {
      return; // No actual change, ignore
    }

    const updateList = [{ field, value }];
    const positionBudgetFieldDef = dbObj?.GetFieldDefByID(kPositionBudgetedSalary);
    const positionBudgetFieldName = positionBudgetFieldDef?.GetFieldName();
    const costBandMaxFieldDef = dbObj?.GetFieldDefByID(kPositionCostBandMaximum);
    const costBandMaxFieldName = costBandMaxFieldDef?.GetFieldName();
    const desiredHoldersFieldDef = dbObj?.GetFieldDefByID(kDesiredHolders);
    const desiredHoldersFieldName = desiredHoldersFieldDef?.GetFieldName();

    if (
      positionBudgetFieldName &&
      (field === costBandMaxFieldName || field === desiredHoldersFieldName)
    ) {
      const isPositionBudgetMapped = isUserMappedField(kPositionBudgetedSalary);
      const isCostBandsMapped = isCostBandMapped(kPositionCostBandMaximum);

      // Scenario 4: Cost bands ARE mapped and position budget is NOT mapped
      if (!isPositionBudgetMapped && isCostBandsMapped) {
        const derivedValue = calculateTotalPositionBudget(
          () => 0,
          () =>
            field === costBandMaxFieldName
              ? value
              : GetCurrentFieldValue(costBandMaxFieldName) || 0,
          () =>
            field === desiredHoldersFieldName
              ? value
              : GetCurrentFieldValue(desiredHoldersFieldName) || 1
        );
        updateList.push({ field: positionBudgetFieldName, value: derivedValue.toString() });
      }
    }

    UpdateFieldValue(updateList);
  };

  const GetCurrentFieldValueById = fieldId => {
    const fieldDef = dbObj.GetFieldDefByID(fieldId);
    if (!fieldDef) {
      return null;
    }
    const fieldName = fieldDef.GetFieldName();
    return GetCurrentFieldValue(fieldName);
  };

  const BuildFieldMap = () => {
    const isEditable = !overrideHasConflicts;
    const fieldMap = BuildPositionFieldMap({
      errors,
      dbObj,
      workingOverride,
      changeType,
      manager,
      record,
      getCurrentFieldValue: GetCurrentFieldValue,
      onRemoveParentPosition: handleRemoveParentPosition,
      baseRecordSupervisor: baseRecordSupervisor,
      recordSupervisor: recordSupervisor,
      GetExistingFieldChanges,
      unsavedFieldChange,
      isMultiEdit,
      multiDataObjs,
      curChanges: changeResults,
      GetRevertButton,
      isEditable,
      queryChartId
    });

    Object.keys(fieldMap).forEach(fieldId => {
      const field = fieldMap[fieldId];
      if (field?.name && field?.fieldDef && field.revertButton) {
        fieldMap[fieldId] = applyRevertButtonPlacement(field, {
          isEditable,
          isDateField: IsDateField(field.fieldDef),
          revertButton: field.revertButton
        });
      }
    });

    return fieldMap;
  };

  const BuildRangeFieldsMap = () => {
    const isEditable = !overrideHasConflicts;
    return BuildPositionRangeFieldsMap({
      errors,
      dbObj,
      workingOverride,
      changeType,
      record,
      getCurrentFieldValue: GetCurrentFieldValue,
      GetExistingFieldChanges,
      unsavedFieldChange,
      GetRevertButton,
      isMultiEdit,
      multiDataObjs,
      curChanges: changeResults,
      isEditable,
      manager
    });
  };

  const BuildAdditionalFieldMap = () => {
    // Return empty object if record is not available yet
    if (
      !record ||
      !dbObj ||
      !workingOverride ||
      !additionalFields ||
      additionalFields.length === 0
    ) {
      return {};
    }

    const fieldMap = {};
    const doc = manager?.GetDoc() || GetDoc();
    const objDB = doc.GetObjDB();
    const positionClass = objDB.GetClassByName(enumObjClassNames.position);
    const basicFieldIds = GetPositionFieldsConfig().map(field => field.id);

    // Filter additional fields (treating them as field IDs)
    const positionAdditionalFields = isInPlanning()
      ? additionalFields
      : additionalFields.filter(fieldName => {
          // Get field definition
          const fieldDef = positionClass.GetFieldDefByName(fieldName);
          const fieldId = fieldDef?.GetFieldID();
          // Make sure it's a position field and not already in the basic fields
          const notInBasicFields = !basicFieldIds.includes(fieldId);
          return fieldDef && notInBasicFields;
        });

    // Build field map similar to BuildPositionFieldMap
    positionAdditionalFields.forEach((fieldName, index) => {
      const fieldDef = positionClass.GetFieldDefByName(fieldName);

      // Skip if field definition doesn't exist
      if (!fieldDef) {
        return;
      }
      // Process the field options
      const fieldTextEditOptions = ProcessAdvancedField(fieldDef, changeType, false, undefined);

      // Get field value
      const fieldValue = GetCurrentFieldValue(fieldName) || "";

      // Check if this field has been changed (for warning status)
      const fieldChanged =
        GetExistingFieldChanges && unsavedFieldChange
          ? GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName)
          : false;

      // Check if this field has errors using smart error detection
      const fieldHasError = errors.some(error => doesErrorApplyToField(error, fieldName));

      // Add the field to the map with position in rows - two fields per row
      const row = Math.floor(index / 2) + 1;
      const order = (index % 2) + 1;

      // Apply isEditable as final override to enableTE
      const isEditable = !overrideHasConflicts;
      const revertButton = GetRevertButton && isEditable ? GetRevertButton(fieldDef) : null;

      const baseField = {
        name: fieldName,
        value: fieldValue,
        textEditOptions: {
          ...fieldTextEditOptions,
          enableTE: fieldTextEditOptions.enableTE && isEditable,
          showTrigger: isEditable,
          row: row,
          order: order,
          width: "50%"
        },
        error: fieldHasError,
        warning: fieldChanged,
        fieldDef: fieldDef
      };

      fieldMap[fieldName] = revertButton
        ? applyRevertButtonPlacement(baseField, {
            isEditable,
            isDateField: IsDateField(fieldDef),
            revertButton
          })
        : baseField;
    });

    return fieldMap;
  };

  const getHeaderTitle = () => {
    if (action === BOX_ACTIONS.REOPEN) {
      return appstr("Reopen position multi");
    }
    if (isMultiEdit && multiDataObjs && multiDataObjs.length > 1) {
      return appstr("Position details");
    }
    return `${appstr("Position details")} - ${sanitizeBlockedValue(dbObj?.SmartGetFieldValue("Position Title"), "")}: ${sanitizeBlockedValue(dbObj?.SmartGetFieldValue("Position ID"), "")}`;
  };

  // Only build the field maps if the record and dependencies are available
  const fieldValuesMap = BuildFieldMap();
  const rangeFieldsMap = BuildRangeFieldsMap();

  const renderAlert = () => {
    const props = (() => {
      if (action === BOX_ACTIONS.REOPEN) {
        return createBoldToastMessage("Reopen position editor changes info", {
          __reopenTheCountPosition__: `reopen the ${(!isMultiEdit ? appstr("Position") : `${multiDataObjs?.length ?? 0} ${appstr("Positions")}`).toLocaleLowerCase()}`,
          __wontBeAutomaticallyReassigned__: `won't be automatically reassigned`
        });
      }
      if (isMultiEdit && multiDataObjs?.length > 1) {
        return createBoldToastMessage("Multi position editor changes info", {
          count: multiDataObjs.length.toString(),
          positions: (multiDataObjs.length === 1
            ? appstr("Position")
            : appstr("Positions")
          ).toLowerCase()
        });
      }
      return null;
    })();

    if (!props) {
      return null;
    }

    return (
      <Alert severity="info" style={{ marginBottom: "16px" }} {...props.alertProps}>
        {props.message}
      </Alert>
    );
  };

  const GetAssigneeDetail = () => {
    const assigneeCount = assignedEmployees.length;
    const maxAssigneeCount = GetCurrentFieldValueById(kDesiredHolders) || 1;

    return {
      label: `${assigneeCount} / ${maxAssigneeCount}`,
      value: Math.round(assigneeCount / maxAssigneeCount),
      assigneeCount,
      maxAssigneeCount
    };
  };

  const assignedEmployeeDetails = GetAssigneeDetail();

  const handleToggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleSidebarModeChange = mode => {
    // If clicking the same mode that's already selected, toggle the sidebar
    if (Array.isArray(mode)) {
      return;
    }

    if (mode === sidebarMode && sidebarExpanded) {
      setSidebarExpanded(false);
    } else {
      setSidebarMode(mode);
      setSidebarExpanded(true);
    }
  };

  //check if there are blocked values in either map
  const hasBlockedValues =
    Object.values(fieldValuesMap).some(field => field.value === kBlockedFieldTag) ||
    Object.values(rangeFieldsMap).some(field => field.value === kBlockedFieldTag);

  // Content mapping for different sidebar modes
  const sidebarContentMap = {
    budget: (
      <BudgetDetailsDrawer
        isOpen={true}
        onClose={() => setSidebarExpanded(false)}
        position={dbObj}
        assignedEmployees={assignedEmployees}
        originalAssignedEmployees={originalAssignedEmployees}
        getPositionFieldValue={GetCurrentFieldValueById}
        dataTestIdPrefix={"position-editor-budget-drawer"}
      />
    )
    // Future tabs can be added here:
    // analytics: <AnalyticsPanel ... />
    // history: <HistoryPanel ... />
  };

  const renderSidebarContent = () => {
    return sidebarContentMap[sidebarMode] || null;
  };

  return (
    <BaseRecordEditorModal
      show={showPositionEditor}
      overrideObj={overrideObj}
      dbObj={dbObj}
      type={type}
      onSave={onSaveRecordEdit}
      onClose={handleClose}
      manager={manager}
      dataTestIdPrefix={"position-editor-"}
      initChanges={initChanges}
      isPlanEdit={true}
      size={{ width: initialWidth, height: initialHeight }}
      otherHeaderProps={{
        size: "xlarge",
        labelSize: "large"
      }}
      otherOverlayProps={{ padding: "16px", labelSize: "large" }}
      validateFields={validateFields}
      getHeaderTitle={getHeaderTitle}
      onSaveWithChanges={handleSave}
      additionalFields={additionalFields}
      controllerData={controllerData}
      onUnsavedChanges={handleUnsavedChanges}
      onRevertOverride={handleRevertOverride}
      onInitializeTarget={handleInitializeTarget}
      onLoadRecordFallback={handleLoadRecordFallback}
      showComments={false}
      includeTopPadding={false}
      includeScrollbar={false}
      isMultiEdit={isMultiEdit}
      action={action}
      allowEdits={!overrideHasConflicts}
      allowSave={!overrideHasConflicts}
    >
      <div
        className={cx({
          "position-editor-with-sidebar": renderSidebar,
          "position-editor-without-sidebar": !renderSidebar,
          "sidebar-expanded": renderSidebar && sidebarExpanded,
          "sidebar-collapsed": renderSidebar && !sidebarExpanded
        })}
        style={{ height: "100%", position: "relative" }}
      >
        {renderSidebar && (
          <PositionEditorLeftSidebar
            isExpanded={sidebarExpanded}
            onToggleExpand={handleToggleSidebar}
            selectedMode={sidebarMode}
            onModeChange={handleSidebarModeChange}
            dataTestIdPrefix="position-editor-left-sidebar"
          >
            {renderSidebarContent()}
          </PositionEditorLeftSidebar>
        )}

        {/* Main Content */}
        <div className="position-editor-main-content">
          <div className="position-editor-main-content-scroll ocn-list-scrollbar">
            <Flex
              direction={"column"}
              gap={"16px"}
              padding={"16px"}
              style={{ minHeight: "100%", width: "100%" }}
            >
              {overrideHasConflicts && (
                <Alert
                  alertActions={{
                    customAction: (
                      <Button
                        dataTestId={"plan-conflicts-shortcut"}
                        size={"small"}
                        modifier={"danger"}
                        onClick={() => {
                          setConflictOverride(workingOverride);
                          setShowConflictModal(true);
                        }}
                      >
                        {appstr("Resolve")}
                      </Button>
                    )
                  }}
                  severity="error"
                  showIcon
                  className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
                >
                  {appstr("Conflict record detected")}
                </Alert>
              )}
              {hasBlockedValues && !overrideHasConflicts && (
                <Alert
                  severity={"info"}
                  showIcon
                  className={cx("oc-padding-small", "oc-text-small")}
                >
                  {appstr("recordeditorcompaccesstip")}
                </Alert>
              )}
              <div style={{ minHeight: "600px" }}>
                {renderAlert()}

                {isMultiEdit ? (
                  <BasicInfoSection
                    parentObject={recordSupervisor}
                    fieldValuesMap={fieldValuesMap}
                    rangeFieldsMap={rangeFieldsMap}
                    onFieldChange={handleFieldValueUpdate}
                    onSearchSelect={searchClickHandler}
                    dataTestIdPrefix={"position-editor"}
                    padding={"0"}
                    queryChartId={queryChartId}
                  />
                ) : (
                  <Accordion
                    name={
                      <Text size={"medium"} modifier={"bold"}>
                        {appstr("Basic info")}
                      </Text>
                    }
                    initExpand={true}
                    iconPosition="right"
                    labelProps={{
                      backgroundColor: "orange",
                      hoverColor: "orange",
                      customStyle: { justifyContent: "space-between", padding: "12px" }
                    }}
                    beforeContent={
                      <BasicInfoSection
                        parentObject={recordSupervisor}
                        fieldValuesMap={fieldValuesMap}
                        rangeFieldsMap={rangeFieldsMap}
                        onFieldChange={handleFieldValueUpdate}
                        onSearchSelect={searchClickHandler}
                        dataTestIdPrefix={"position-editor"}
                        padding={"0"}
                        queryChartId={queryChartId}
                      />
                    }
                  />
                )}

                {!isMultiEdit && (
                  <Accordion
                    name={
                      <Flex alignItems="center" width="100%" gap="4px">
                        <Text size={"medium"} modifier={"bold"}>
                          {appstr("Assignments")}
                        </Text>
                        {dbObj && (
                          <span style={{ width: "100%" }}>
                            <Pill
                              className={cx("oc-relative", "oc-fit-content", "oc-status-pill")}
                              label={assignedEmployeeDetails.label}
                              color={
                                assignedEmployeeDetails.assigneeCount <
                                assignedEmployeeDetails.maxAssigneeCount
                                  ? "warning" // Under-assigned
                                  : assignedEmployeeDetails.assigneeCount >
                                      assignedEmployeeDetails.maxAssigneeCount
                                    ? "error" // Over-assigned
                                    : "success" // Correctly assigned
                              }
                            />
                          </span>
                        )}
                      </Flex>
                    }
                    initExpand={true}
                    iconPosition="right"
                    labelProps={{
                      backgroundColor: "orange",
                      hoverColor: "orange",
                      customStyle: {
                        justifyContent: "space-between",
                        padding: "12px",
                        marginTop: "16px"
                      }
                    }}
                    beforeContent={
                      <AssignmentSection
                        assignedEmployees={assignedEmployees}
                        onAssignEmployee={handleAssignEmployee}
                        onRemoveEmployee={handleRemoveEmployeeWrapper}
                        dataTestIdPrefix={"position-editor-assignment"}
                        padding={"0"}
                        disabled={IsAppendTableContext(manager) || overrideHasConflicts}
                      />
                    }
                  />
                )}
                {/* Additional Info section */}
                {(() => {
                  const additionalFieldMap = BuildAdditionalFieldMap();
                  return isInPlanning() || Object.keys(additionalFieldMap).length > 0 ? (
                    <Accordion
                      name={
                        <Text size={"medium"} modifier={"bold"}>
                          {appstr("Additional fields")}
                        </Text>
                      }
                      initExpand={true}
                      iconPosition="right"
                      labelProps={{
                        backgroundColor: "orange",
                        hoverColor: "orange",
                        customStyle: {
                          justifyContent: "space-between",
                          padding: "12px",
                          marginTop: "16px"
                        }
                      }}
                      beforeContent={
                        isInPlanning() ? (
                          <AdditionalInfoSection
                            parentObject={baseRecordSupervisor}
                            fieldValuesMap={additionalFieldMap}
                            onFieldChange={handleFieldValueUpdate}
                            onSearchSelect={searchClickHandler}
                            canManageFields={canManageFields}
                          />
                        ) : (
                          <BasicInfoSection
                            parentObject={baseRecordSupervisor}
                            fieldValuesMap={additionalFieldMap}
                            onFieldChange={handleFieldValueUpdate}
                            onSearchSelect={searchClickHandler}
                            dataTestIdPrefix={"position-editor-additional"}
                            padding={"0"}
                            queryChartId={queryChartId}
                          />
                        )
                      }
                    />
                  ) : null;
                })()}
              </div>
              {!isMultiEdit && <Divider />}
            </Flex>
          </div>
        </div>
      </div>

      {/* Conflict Assignment Modal */}
      {conflicts.length > 0 && dbObj && (
        <ConflictAssignmentModal
          show={conflicts.length > 0}
          conflicts={conflicts}
          targetPosition={dbObj}
          onConfirm={() => {
            forceAssignEmployees(conflicts);
            setConflicts([]);
          }}
          onCancel={() => {
            setConflicts([]);
          }}
        />
      )}
    </BaseRecordEditorModal>
  );
};

export default PositionEditorModal;
